import { Component, EventEmitter, Input, OnInit, Output, WritableSignal, computed, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '../../../core/services/api.service';
import { Spirit } from '../../../core/models/spirit.model';
import { PriceComponent } from '../../../shared/components/price/price.component';
import { SpiritImagePipe } from '../../../core/pipes/spiritImage';
import { KeyValuePipe } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SpiritDetailCharacteristicComponent } from './spirit-detail-characteristic/spirit-detail-characteristic.component';
import { LocalService } from '../../../core/services/local.service';
import { SpiritTranslationPipe } from '../../../core/pipes/spiritTranslation';
import { CatalogTranslationPipe } from '../../../core/pipes/catalogTranslation';
import { CatalogSpirit } from '../../../core/models/catalogSpirit.model';
import { SpiritDetailLockerComponent } from './spirit-detail-locker/spirit-detail-locker.component';
import { LockerCartAddIcon } from "../../../shared/icons/locker-cart-add/locker-cart-add.component";
import { MatBadgeModule } from '@angular/material/badge';
import { SpiritEditorialComponent } from './spirit-editorial/spirit-editorial.component';
import {
  EditorialContent, hasAffinitiesBlock, hasCharacterBlock, hasIngredientsBlock,
  hasNarrativeBlock, hasProductionBlock, hasSensoryBlock, hasTastingBlock
} from '../../../core/models/editorialContent.model';

const materialModules = [
  MatIconModule,
  MatButtonModule,
  MatBadgeModule
]

const components = [
  SpiritDetailCharacteristicComponent,
  SpiritDetailLockerComponent,
  SpiritEditorialComponent
]

const pipes = [
  SpiritTranslationPipe,
  CatalogTranslationPipe
]

const icons = [
  LockerCartAddIcon
]

@Component({
  selector: 'app-spirit-detail',
  standalone: true,
  imports: [
    ...materialModules, ...components, ...pipes, ...icons, PriceComponent, SpiritImagePipe, TranslateModule
  ],
  templateUrl: './spirit-detail.component.html',
  styleUrl: './spirit-detail.component.scss'
})
export class SpiritDetailComponent implements OnInit {

  private api = inject(ApiService);
  private local = inject(LocalService);

  @Input() spiritSlugname: string = '';

  menu: WritableSignal<any> = signal(null);
  spirit: WritableSignal<Spirit | null> = signal(null);
  catalogSpirit: WritableSignal<CatalogSpirit | null> = signal(null);

  /**
   * Present only when the backend served editorial content for the requested locale. Its
   * absence, or the absence of a single block inside it, is the signal to keep showing the
   * matching legacy text.
   */
  editorialContent: WritableSignal<EditorialContent | null> = signal(null);

  /**
   * Fallback is decided PER BLOCK, never by the presence of the `editorialContent` object.
   *
   * The distinction is not cosmetic: prose, sensory profile, character and affinities are
   * produced by different operations that fail independently. A spirit whose assessment
   * published axes but whose editorial run has not finished must keep its legacy description,
   * and one gated on "is there any editorial content" would lose it.
   */
  showLegacyDescription = computed(() => !hasNarrativeBlock(this.editorialContent()));
  showLegacyTastingNotes = computed(() => !hasTastingBlock(this.editorialContent()));
  showLegacyElaboration = computed(() => !hasProductionBlock(this.editorialContent()));
  showLegacyIngredients = computed(() => !hasIngredientsBlock(this.editorialContent()));
  /** Legacy body/richness/smoke/sweetness scales, replaced by the published axes. */
  showLegacyScales = computed(() => !hasSensoryBlock(this.editorialContent()));
  showLegacyCharacters = computed(() => !hasCharacterBlock(this.editorialContent()));
  showLegacyFoodPairings = computed(() => !hasAffinitiesBlock(this.editorialContent()));

  /**
   * Brand, age, volume and capacity are printed by the editorial "product information" block,
   * which is rendered whenever there is an editorial sheet at all: it carries the catalogue rows
   * even when nothing else was published. Without that sheet the general list is the only place
   * those rows can appear, so it keeps them.
   *
   * This is the ONLY fallback keyed on the presence of the object rather than on a block, and it
   * is not an exception to the per-block rule: the destination of these rows is the catalogue
   * itself, not an editorial block that may or may not have been generated.
   */
  showLegacyIdentity = computed(() => this.editorialContent() === null);

  lockerUser: any = null;
  lockerCart: any[] = [];

  constructor(
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    const lockerUser = this.local.getData('lockerUser');
    if (lockerUser) {
      this.lockerUser = JSON.parse(lockerUser);
      this.getLockerCart();
    }

    const detailSpirit = this.local.getDataWithExpiration('detailSpirit');
    if (detailSpirit) {
      this.spirit.set(JSON.parse(detailSpirit));
      this.local.removeData('detailSpirit');
    }
    this.getSpirit();
    this.getMenu();
  }

  getMenu() {
    this.api.getMenu().subscribe(
      data => {
        this.menu.set(data);
      }
    );
  }

  getSpirit() {
    this.api.getCompanySpirit(this.spiritSlugname).subscribe(
      data => {
        this.spirit.set(data);
        this.catalogSpirit.set(data.catalogSpirit);
        this.editorialContent.set(data.editorialContent ?? null);
      }
    );
  }

  goBack() {
    this.router.navigate(['../'], {relativeTo: this.route});
  }

  favorite(spirit: Spirit) {
    spirit.fav = !spirit.fav;
    this.local.manageFavorites(spirit);
    this.local.saveData('favUpdatedSpirit', JSON.stringify(spirit));
  }

  getLockerCart() {
    const lockerCartString = this.local.getData('lockerCart');
    if (lockerCartString) {
      this.lockerCart = JSON.parse(lockerCartString);
    }
  }

  goToLockerCart() {
    this.router.navigate(['../../locker-cart'], {relativeTo: this.route});
  }
}
